import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { capabilityTooltip, hasFrontendPermission } from '../permissions';

const STATUS_OPTIONS = ['pending', 'accepted', 'rejected'];

const STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
};

const makeEmptyDraft = () => ({
  customerId: '',
  title: '',
  description: '',
  valid_until: '',
  items: [{ description: '', quantity: 1, unit_price: 0 }],
});

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const totalForItems = (items = []) => items.reduce(
  (sum, item) => sum + (Number(item.quantity || 0) * Number(item.unit_price || 0)),
  0,
);

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

function QuoteStat({ label, value, tone = 'default' }) {
  return (
    <article className={`quotes-stat quotes-stat--${tone}`}>
      <span className="quotes-stat-label">{label}</span>
      <strong className="quotes-stat-value">{value}</strong>
    </article>
  );
}

function QuoteField({ label, children }) {
  return (
    <label className="quotes-field">
      <span className="quotes-field-label">{label}</span>
      {children}
    </label>
  );
}

function QuoteListItem({ quote, active, customerName, onSelect }) {
  return (
    <button
      type="button"
      className={`quotes-list-item${active ? ' is-active' : ''}`}
      onClick={() => onSelect(quote.id)}
    >
      <div className="quotes-list-topline">
        <strong className="quotes-list-title">{quote.title || quote.id}</strong>
        <span className={`quotes-status-badge quotes-status-badge--${String(quote.status || '').toLowerCase()}`}>
          {STATUS_LABELS[quote.status] || quote.status || 'Pending'}
        </span>
      </div>
      <div className="quotes-list-meta">
        <span>{quote.id}</span>
        <span>{customerName}</span>
      </div>
      <div className="quotes-list-meta">
        <span>{formatDate(quote.created_at)}</span>
        <span>{money(quote.total_amount)}</span>
      </div>
    </button>
  );
}

export function QuotesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageQuotes = hasFrontendPermission(user, 'quotes.manage');
  const canDeleteQuotes = hasFrontendPermission(user, 'quotes.delete.any');

  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [draft, setDraft] = useState(makeEmptyDraft);
  const [convertDraft, setConvertDraft] = useState({ quoteId: '', scheduledDate: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const createCardRef = useRef(null);
  const createCustomerInputRef = useRef(null);

  const closeForm = useCallback(() => {
    setDraft(makeEmptyDraft());
    setEditingId('');
    setShowForm(false);
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('compose') === '1') {
      navigate('/quotes', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [quotesData, customersData] = await Promise.all([
        apiFetch('/api/quotes', { token }),
        apiFetch('/api/customers', { token }),
      ]);
      setQuotes(Array.isArray(quotesData) ? quotesData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (e) {
      setError(e.message || 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  useEffect(() => {
    if (!canManageQuotes) return;
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('compose') !== '1') return;
    setEditingId('');
    setDraft(makeEmptyDraft());
    setShowForm(true);
    window.requestAnimationFrame(() => {
      createCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      createCustomerInputRef.current?.focus();
    });
  }, [canManageQuotes, location.search]);

  const getCustomerName = useCallback((customerId) => {
    const customer = customers.find((item) => item.id === customerId);
    return customer?.name || customerId || 'No customer';
  }, [customers]);

  const filteredQuotes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return quotes.filter((quote) => {
      if (statusFilter && quote.status !== statusFilter) return false;
      if (!term) return true;
      return [quote.id, quote.title, getCustomerName(quote.customerId)]
        .some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [quotes, searchTerm, statusFilter, getCustomerName]);

  useEffect(() => {
    if (!filteredQuotes.length) {
      setSelectedQuoteId('');
      return;
    }
    if (!filteredQuotes.some((quote) => quote.id === selectedQuoteId)) {
      setSelectedQuoteId(filteredQuotes[0].id);
    }
  }, [filteredQuotes, selectedQuoteId]);

  const selectedQuote = useMemo(
    () => filteredQuotes.find((quote) => quote.id === selectedQuoteId) || null,
    [filteredQuotes, selectedQuoteId],
  );

  const stats = useMemo(() => ({
    total: quotes.length,
    pending: quotes.filter((quote) => quote.status === 'pending').length,
    accepted: quotes.filter((quote) => quote.status === 'accepted').length,
    totalValue: quotes.reduce((sum, quote) => sum + Number(quote.total_amount || 0), 0),
  }), [quotes]);
  const quickActions = useMemo(() => {
    const actions = [];
    if (canManageQuotes) {
      actions.push({
        label: showForm ? 'Hide Quote Form' : 'Create Quote',
        onClick: () => setShowForm((value) => !value),
      });
    }
    if (stats.pending > 0) {
      actions.push({
        label: 'Review Pending',
        onClick: () => setStatusFilter('pending'),
      });
    }
    if (stats.accepted > 0) {
      actions.push({
        label: 'Open Accepted',
        onClick: () => {
          setStatusFilter('accepted');
          const acceptedQuote = quotes.find((quote) => quote.status === 'accepted');
          if (acceptedQuote) setSelectedQuoteId(acceptedQuote.id);
        },
      });
    }
    return actions.slice(0, 3);
  }, [canManageQuotes, showForm, stats.pending, stats.accepted, quotes]);

  const handleRefresh = () => {
    setRefreshKey((value) => value + 1);
  };

  const openEditForm = (quote) => {
    setDraft({
      customerId: quote.customerId || '',
      title: quote.title || '',
      description: quote.description || '',
      valid_until: quote.valid_until || '',
      items: Array.isArray(quote.items) && quote.items.length ? quote.items : [{ description: '', quantity: 1, unit_price: 0 }],
    });
    setEditingId(quote.id);
    setShowForm(true);
  };

  const handleSaveQuote = async (event) => {
    event.preventDefault();
    if (!token || !canManageQuotes) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...draft,
        total_amount: totalForItems(draft.items),
      };
      if (editingId) {
        await apiFetch(`/api/quotes/${encodeURIComponent(editingId)}`, { token, method: 'PUT', body: payload });
      } else {
        await apiFetch('/api/quotes', { token, method: 'POST', body: payload });
      }
      closeForm();
      setRefreshKey((value) => value + 1);
      pushToast(editingId ? 'Quote updated' : 'Quote created', 'success');
    } catch (e) {
      setError(e.message || 'Failed to save quote');
      pushToast(e.message || 'Failed to save quote', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuote = async () => {
    if (!token || !canDeleteQuotes || !selectedQuote) return;
    if (!window.confirm(`Delete ${selectedQuote.title || selectedQuote.id}?`)) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/quotes/${encodeURIComponent(selectedQuote.id)}`, { token, method: 'DELETE' });
      setQuotes((prev) => prev.filter((quote) => quote.id !== selectedQuote.id));
      pushToast(`Deleted ${selectedQuote.id}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to delete quote');
      pushToast(e.message || 'Failed to delete quote', 'error');
    } finally {
      setSaving(false);
    }
  };

  const mutateQuote = async (quoteId, action) => {
    if (!token || !quoteId) return;
    setSaving(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/quotes/${encodeURIComponent(quoteId)}/${action}`, { token, method: 'POST' });
      setQuotes((prev) => prev.map((quote) => quote.id === updated.id ? updated : quote));
      pushToast(`Quote ${action}ed`, 'success');
    } catch (e) {
      setError(e.message || `Failed to ${action} quote`);
      pushToast(e.message || `Failed to ${action} quote`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const openConvertForm = (quote) => {
    setConvertDraft({
      quoteId: quote.id,
      scheduledDate: new Date().toISOString().split('T')[0],
    });
  };

  const closeConvertForm = () => {
    setConvertDraft({ quoteId: '', scheduledDate: '' });
  };

  const handleConvert = async () => {
    if (!token || !convertDraft.quoteId || !convertDraft.scheduledDate) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/quotes/${encodeURIComponent(convertDraft.quoteId)}/convert`, {
        token,
        method: 'POST',
        body: { scheduledDate: convertDraft.scheduledDate },
      });
      closeConvertForm();
      setRefreshKey((value) => value + 1);
      pushToast('Quote converted to job', 'success');
    } catch (e) {
      setError(e.message || 'Failed to convert quote');
      pushToast(e.message || 'Failed to convert quote', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (index, key, value) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item),
    }));
  };

  const addLineItem = () => {
    setDraft((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  if (loading) {
    return (
      <section className="quotes-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="quotes-page">
      <div className="quotes-header">
        <div>
          <span className="quotes-eyebrow">Pipeline</span>
          <h1 className="quotes-heading">Quotes</h1>
          <p className="quotes-subheading">
            Estimate pipeline, approval status, and conversion-ready customer proposals.
          </p>
        </div>
        <div className="quotes-header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon />
            Refresh
          </Button>
          <Button
            onClick={() => setShowForm((value) => !value)}
            disabled={!canManageQuotes}
            title={!canManageQuotes ? capabilityTooltip('quotes.manage') : 'Create quote'}
          >
            <PlusIcon />
            New Quote
          </Button>
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadData} /> : null}

      <Card className="workflow-guide-card" title="What To Do Here">
        <div className="workflow-guide-head">
          <p className="workflow-guide-copy">
            Use Quotes to keep proposals moving from draft to approval, then convert accepted work into real scheduled jobs.
          </p>
          <div className="workflow-guide-actions">
            {quickActions.map((action) => (
              <Button key={action.label} variant="secondary" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="workflow-guide-list">
          <div className="workflow-guide-item">Start with pending quotes so customer decisions do not stall the pipeline.</div>
          <div className="workflow-guide-item">Check pricing, dates, and line items in the detail panel before accepting or rejecting.</div>
          <div className="workflow-guide-item">Convert accepted quotes into jobs quickly so the work can move into scheduling and dispatch.</div>
        </div>
      </Card>

      <div className="quotes-stats-grid">
        <QuoteStat label="Quotes" value={stats.total} />
        <QuoteStat label="Pending" value={stats.pending} tone="warning" />
        <QuoteStat label="Accepted" value={stats.accepted} tone="accent" />
        <QuoteStat label="Pipeline Value" value={money(stats.totalValue)} tone="info" />
      </div>

      {showForm ? (
        <div ref={createCardRef}>
        <Card className="quotes-create-card" title={editingId ? 'Edit Quote' : 'Create Quote'}>
          <form className="quotes-form-shell" onSubmit={handleSaveQuote}>
            <div className="quotes-form-grid">
              <QuoteField label="Customer">
                <select ref={createCustomerInputRef} className="quotes-input" value={draft.customerId} onChange={(event) => setDraft((prev) => ({ ...prev, customerId: event.target.value }))} required>
                  <option value="">Select customer</option>
                  {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                </select>
              </QuoteField>
              <QuoteField label="Title">
                <input className="quotes-input" value={draft.title} onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))} required />
              </QuoteField>
              <QuoteField label="Valid Until">
                <input className="quotes-input" type="date" value={draft.valid_until} onChange={(event) => setDraft((prev) => ({ ...prev, valid_until: event.target.value }))} />
              </QuoteField>
              <QuoteField label="Description">
                <textarea className="quotes-input quotes-textarea" value={draft.description} onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))} />
              </QuoteField>
            </div>

            <div className="quotes-line-items">
              <div className="quotes-line-items-head">
                <h3 className="quotes-line-items-title">Line Items</h3>
                <Button size="sm" variant="secondary" onClick={addLineItem}>Add Line</Button>
              </div>
              {draft.items.map((item, index) => (
                <div className="quotes-line-item-row" key={`${index}-${item.description}`}>
                  <input className="quotes-input" value={item.description} onChange={(event) => updateItem(index, 'description', event.target.value)} placeholder="Description" />
                  <input className="quotes-input" type="number" min="1" value={item.quantity} onChange={(event) => updateItem(index, 'quantity', Number(event.target.value || 0))} />
                  <input className="quotes-input" type="number" min="0" step="0.01" value={item.unit_price} onChange={(event) => updateItem(index, 'unit_price', Number(event.target.value || 0))} />
                  <div className="quotes-line-total">{money(Number(item.quantity || 0) * Number(item.unit_price || 0))}</div>
                  <Button size="sm" variant="danger" onClick={() => removeLineItem(index)} disabled={draft.items.length === 1}>Remove</Button>
                </div>
              ))}
              <div className="quotes-line-items-foot">
                <span>Total</span>
                <strong>{money(totalForItems(draft.items))}</strong>
              </div>
            </div>

            <div className="quotes-form-actions">
              <Button variant="ghost" onClick={closeForm}>Cancel</Button>
              <Button type="submit" disabled={saving}>{editingId ? 'Update Quote' : 'Create Quote'}</Button>
            </div>
          </form>
        </Card>
        </div>
      ) : null}

      {convertDraft.quoteId ? (
        <Card className="quotes-convert-card" title="Convert Quote To Job">
          <div className="quotes-convert-body">
            <QuoteField label="Scheduled Date">
              <input className="quotes-input" type="date" value={convertDraft.scheduledDate} onChange={(event) => setConvertDraft((prev) => ({ ...prev, scheduledDate: event.target.value }))} />
            </QuoteField>
            <div className="quotes-form-actions">
              <Button variant="ghost" onClick={closeConvertForm}>Cancel</Button>
              <Button onClick={handleConvert} disabled={saving || !convertDraft.scheduledDate}>Create Job</Button>
            </div>
          </div>
        </Card>
      ) : null}

      <div className="quotes-layout">
        <Card className="quotes-list-card" title="Quote Queue">
          <div className="quotes-toolbar">
            <input className="quotes-input" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search by quote, title, or customer" />
            <select className="quotes-input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{STATUS_LABELS[option]}</option>)}
            </select>
          </div>

          {filteredQuotes.length ? (
            <div className="quotes-list">
              {filteredQuotes.map((quote) => (
                <QuoteListItem key={quote.id} quote={quote} active={quote.id === selectedQuoteId} customerName={getCustomerName(quote.customerId)} onSelect={setSelectedQuoteId} />
              ))}
            </div>
          ) : (
            <EmptyState title="No quotes in this view" message="Adjust filters or create a new quote." />
          )}
        </Card>

        <Card className="quotes-detail-card" title={selectedQuote ? selectedQuote.title || selectedQuote.id : 'Details'}>
          {!selectedQuote ? (
            <EmptyState title="No quote selected" message="Choose a quote to review its customer, approval state, and line items." />
          ) : (
            <div className="quotes-detail">
              <div className="quotes-detail-head">
                <div className="quotes-detail-topline">
                  <span className="quotes-item-id">{selectedQuote.id}</span>
                  <span className={`quotes-status-badge quotes-status-badge--${String(selectedQuote.status || '').toLowerCase()}`}>
                    {STATUS_LABELS[selectedQuote.status] || selectedQuote.status || 'Pending'}
                  </span>
                </div>
                <strong className="quotes-detail-amount">{money(selectedQuote.total_amount)}</strong>
                <p className="quotes-detail-copy">{selectedQuote.description || 'No quote description captured yet.'}</p>
              </div>

              <div className="quotes-detail-grid">
                <QuoteField label="Customer">
                  <div className="quotes-readout">{getCustomerName(selectedQuote.customerId)}</div>
                </QuoteField>
                <QuoteField label="Created">
                  <div className="quotes-readout">{formatDate(selectedQuote.created_at)}</div>
                </QuoteField>
                <QuoteField label="Valid Until">
                  <div className="quotes-readout">{formatDate(selectedQuote.valid_until)}</div>
                </QuoteField>
                <QuoteField label="Job Created">
                  <div className="quotes-readout">{selectedQuote.jobId || 'Not converted'}</div>
                </QuoteField>
              </div>

              <div className="quotes-line-items-list">
                {(selectedQuote.items || []).map((item, index) => (
                  <div className="quotes-line-item-display" key={`${selectedQuote.id}-${index}`}>
                    <span>{item.description || 'Untitled line'}</span>
                    <span>{item.quantity} x {money(item.unit_price)}</span>
                    <strong>{money(Number(item.quantity || 0) * Number(item.unit_price || 0))}</strong>
                  </div>
                ))}
                {(!selectedQuote.items || selectedQuote.items.length === 0) ? (
                  <EmptyState title="No line items" message="This quote does not include itemized pricing yet." />
                ) : null}
              </div>

              <div className="quotes-detail-actions">
                {canManageQuotes && selectedQuote.status === 'pending' ? (
                  <>
                    <Button onClick={() => mutateQuote(selectedQuote.id, 'accept')} disabled={saving}>Accept</Button>
                    <Button variant="danger" onClick={() => mutateQuote(selectedQuote.id, 'reject')} disabled={saving}>Reject</Button>
                  </>
                ) : null}
                {canManageQuotes && selectedQuote.status === 'accepted' && !selectedQuote.jobId ? (
                  <Button variant="secondary" onClick={() => openConvertForm(selectedQuote)} disabled={saving}>Convert To Job</Button>
                ) : null}
                {canManageQuotes ? (
                  <Button variant="secondary" onClick={() => openEditForm(selectedQuote)} disabled={saving}>Edit</Button>
                ) : null}
                <Button
                  variant="danger"
                  onClick={handleDeleteQuote}
                  disabled={!canDeleteQuotes || saving}
                  title={!canDeleteQuotes ? capabilityTooltip('quotes.delete.any') : 'Delete quote'}
                >
                  Delete
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
